import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {InformationUnitModel} from 'app/core/modules/rest/informationunit/response/information-unit.model';
import {getTextBasedOnLanguage, LanguageEnum} from 'app/core/enums/language.enum';
import {UpdateInformationUnitModel} from 'app/core/modules/rest/informationunit/request/update-information-unit.model';
import {FormControl, FormGroup} from '@angular/forms';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {PopupTypeEnum} from 'app/core/enums/popup-type.enum';
import {processHtml} from '../../../../display-consent/helpers/unit-parsing';
import {InformationUnitRestService} from 'app/core/modules/rest/informationunit/information-unit-rest.service';
import {take} from 'rxjs/operators';
import {PreviewUnitWithPopupResponseModel} from 'app/core/modules/rest/informationunit/response/preview-unit-with-popup-response.model';
import {AbstractDialogModel} from 'app/shared/dialogs/model/abstract-dialog.model';
import {PreviewUnitWithPopupRequestModel} from 'app/core/modules/rest/informationunit/request/preview-unit-with-popup-request.model';
import {LanguageStyleEnum} from 'app/core/enums/language-style.enum';
import {UnitPopupWithPopupComponent} from 'app/shared/dialogs/unit-popup/unit-popup-with-popup/unit-popup-with-popup.component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {UnitPopupComponent} from 'app/shared/dialogs/unit-popup/unit-popup.component';
import {NotificationService} from 'app/shared/components/notification/service/notification.service';
import {TranslateService} from '@ngx-translate/core';

@UntilDestroy()
@Component({
    selector: 'app-information-unit-editor',
    templateUrl: './information-unit-editor.component.html',
    styleUrls: ['./information-unit-editor.component.scss']
})
export class InformationUnitEditorComponent implements OnInit, OnChanges {

    @Input() currentUnitOrderId: number;
    @Input() informationUnit: InformationUnitModel;
    @Input() language: LanguageEnum;
    @Input() indicationId: string | undefined;
    @Output() saveInformationUnitChangesEvent = new EventEmitter<UpdateInformationUnitModel>();
    @Output() discardChangesEvent = new EventEmitter<void>();
    @Output() changeLanguageEvent = new EventEmitter<LanguageEnum>();

    langDe = LanguageEnum.GERMAN;
    langEn = LanguageEnum.ENGLISH_GB;
    langSr = LanguageEnum.SERBIAN;
    titleRows = 1;
    contentRows = 15;
    selectedLanguage = LanguageEnum.GERMAN;

    form: FormGroup;
    titleStandard: FormControl = new FormControl('', []);
    infoStandard: FormControl = new FormControl('', []);
    titleSimple: FormControl = new FormControl('', []);
    infoSimple: FormControl = new FormControl('', []);
    titleEasy: FormControl = new FormControl('', []);
    infoEasy: FormControl = new FormControl('', []);

    unitPopUps: AbstractDialogModel[] | undefined;
    styleStandard = LanguageStyleEnum.STANDARD;
    styleSimple = LanguageStyleEnum.SIMPLE;
    styleEasy = LanguageStyleEnum.EASY;

    console = console;

    constructor(private dialog: MatDialog, private popUpDialog: MatDialog, private informationUnitService: InformationUnitRestService,
                private notificationService: NotificationService, private translate: TranslateService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes.informationUnit || changes.language) && this.informationUnit) {
            this.populateForm();
        }
    }

    ngOnInit(): void {
        this.initializeForm();
    }

    private initializeForm(): void {
        this.form = new FormGroup({
            titleStandard: this.titleStandard,
            infoStandard: this.infoStandard,
            titleSimple: this.titleSimple,
            infoSimple: this.infoSimple,
            titleEasy: this.titleEasy,
            infoEasy: this.infoEasy
        });
    }

    selectLanguage(selectedLanguage: LanguageEnum): void {
        this.selectedLanguage = selectedLanguage;
        this.changeLanguageEvent.emit(selectedLanguage);
    }

    private detectedChangesInForm(): boolean {
        return this.form.dirty;
    }

    private populateForm(): void {
        this.initializeForm();
        if (this.informationUnit) {
            this.form.setValue({
                titleStandard: getTextBasedOnLanguage(this.language, this.informationUnit.titleStandardEn, this.informationUnit.titleStandardDe, this.informationUnit.titleStandardSr),
                infoStandard: getTextBasedOnLanguage(this.language, this.informationUnit.infoStandardEn, this.informationUnit.infoStandardDe, this.informationUnit.infoStandardSr),

                titleSimple: getTextBasedOnLanguage(this.language, this.informationUnit.titleSimpleEn, this.informationUnit.titleSimpleDe, this.informationUnit.titleSimpleSr),
                infoSimple: getTextBasedOnLanguage(this.language, this.informationUnit.infoSimpleEn, this.informationUnit.infoSimpleDe, this.informationUnit.infoSimpleSr),

                titleEasy: getTextBasedOnLanguage(this.language, this.informationUnit.titleEasyEn, this.informationUnit.titleEasyDe, this.informationUnit.titleEasySr),
                infoEasy: getTextBasedOnLanguage(this.language, this.informationUnit.infoEasyEn, this.informationUnit.infoEasyDe, this.informationUnit.infoEasySr)
            });
        }
    }

    private resetForm(): void {
        this.informationUnit = null;
        this.form.setValue({
            titleStandard: '',
            infoStandard: '',

            titleSimple: '',
            infoSimple: '',

            titleEasy: '',
            infoEasy: ''
        });
    }

    save(): void {
        const request: UpdateInformationUnitModel = {
            id: this.informationUnit?.id,
            language: this.language,
            indicationId: this.indicationId,

            titleStandard: this.form.value.titleStandard,
            infoStandard: this.form.value.infoStandard,

            titleSimple: this.form.value.titleSimple,
            infoSimple: this.form.value.infoSimple,

            titleEasy: this.form.value.titleEasy,
            infoEasy: this.form.value.infoEasy
        };
        this.saveInformationUnitChangesEvent.emit(request);
    }

    discard(): void {
        this.discardChangesEvent.emit();
    }

    onPreviewUnit(title: string, info: string, languageStyle: LanguageStyleEnum): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.panelClass = 'abstract-dialog-container';

        const request: PreviewUnitWithPopupRequestModel = {
            indicationId: this.indicationId,
            language: this.selectedLanguage,
            languageStyle,
            text: info
        };

        this.informationUnitService.previewInformationUnitWithPopUps(request).pipe(take(1)).subscribe(data => {
            const preview: PreviewUnitWithPopupResponseModel = data;
            this.unitPopUps = preview.popUps;

            const cleanContent = this.cleanContent(preview.content);

            dialogConfig.data = {
                type: PopupTypeEnum.UNIT,
                title,
                indicationId: this.indicationId,
                content: cleanContent,
                disableVoice: true
            };
            const dialogRef = this.dialog.open(UnitPopupWithPopupComponent, dialogConfig);
            dialogRef.componentInstance.openDialog.pipe(untilDestroyed(this)).subscribe((abstractModel) => {
                this.openNewPopUp(abstractModel);
            });
        });
    }

    openNewPopUp(data: AbstractDialogModel): void {
        const newDialogConfig = new MatDialogConfig();
        newDialogConfig.disableClose = false;
        newDialogConfig.panelClass = 'abstract-dialog-container';

        const images = data.id.split('_');
        const firstThatExist = images[0].toLowerCase();

        const popUpInfo = this.unitPopUps?.find(element => (element.id === firstThatExist && element.type === data.type));
        if (popUpInfo === undefined) {
            this.notificationService.info(this.translate.instant('NOTIFICATION.NO_POPUP_IMAGE'));
            return;
        }

        const cleanContent = this.cleanContent(popUpInfo.content);

        newDialogConfig.data = {
            id: data.id,
            indicationId: this.indicationId,
            type: popUpInfo.type,
            title: popUpInfo.type === PopupTypeEnum.TEXT ? data.title : popUpInfo.title,
            content: cleanContent,
            language: this.language,
            disableVoice: true
        };
        this.popUpDialog.open(UnitPopupComponent, newDialogConfig);
    }

    cleanContent(content: string): string {
        let cleanContent = content.replace(/\[(.*?)\]/gi, '');
        cleanContent = cleanContent.replace(/<b>&boxbox;<\/b>/g, '');
        cleanContent = cleanContent.replace(' ,', ',');

        const customBR = '[br]';
        cleanContent = cleanContent.replace(/<br>/g, customBR);
        const html: HTMLElement = processHtml(cleanContent);
        return html.innerHTML.replace(/\[br\]/g, '<br>');
    }
}
